import { TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { isMondayDateString, isValidISODate, MONDAY_ONLY_WEEK_START_ERROR } from '../report-utils';

interface MondayWeekStartFieldProps {
  value: string;
  onChange: (mondayIsoDate: string) => void;
  label?: string;
  minWidth?: number;
}

/** Result of validating a week-start date change (exported for unit tests). */
export function getMondayWeekStartChangeResult(
  raw: string,
  currentValue: string
):
  | { type: 'error'; error: string }
  | { type: 'noop' }
  | { type: 'valid'; notifyParent: boolean; value: string } {
  if (!raw) {
    return { type: 'error', error: MONDAY_ONLY_WEEK_START_ERROR };
  }
  if (!isValidISODate(raw)) {
    return { type: 'noop' };
  }
  if (!isMondayDateString(raw)) {
    return { type: 'error', error: MONDAY_ONLY_WEEK_START_ERROR };
  }
  return { type: 'valid', notifyParent: raw !== currentValue, value: raw };
}

/**
 * Week-start date picker: only Mondays are accepted (UDOMI-921, UDOMI-924).
 */
export function MondayWeekStartField({
  value,
  onChange,
  label = 'Week Starting (Monday)',
  minWidth = 200,
}: MondayWeekStartFieldProps) {
  const [error, setError] = useState<string | undefined>();

  const handleChange = (raw: string) => {
    const result = getMondayWeekStartChangeResult(raw, value);
    if (result.type === 'error') {
      setError(result.error);
      return;
    }
    if (result.type === 'noop') {
      return;
    }
    // UDOMI-1011: clear sticky validation when user re-selects a Monday (including the same date).
    setError(undefined);
    if (result.notifyParent) {
      onChange(result.value);
    }
  };

  const handleBlur = () => {
    if (value && !isMondayDateString(value)) {
      setError(MONDAY_ONLY_WEEK_START_ERROR);
    }
  };

  return (
    <TextField
      label={label}
      type="date"
      value={value}
      onChange={e => handleChange(e.target.value)}
      onBlur={handleBlur}
      size="small"
      error={Boolean(error)}
      helperText={error ?? `Week of ${formatWeekRange(value)}`}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth }}
    />
  );
}

function formatWeekRange(mondayIso: string): string {
  if (!isValidISODate(mondayIso)) {
    return 'select a Monday';
  }
  const monday = DateTime.fromISO(mondayIso);
  const friday = monday.plus({ days: 4 });
  return `${monday.toFormat('MMM d')} – ${friday.toFormat('MMM d, yyyy')}`;
}
